
import {
	getEnhancedMapping,
	IEnhancedMapping,
	IMap,
	ILayer,
	ILatLng,
	ILatLngBounds,
	convertStringToLatLng,
	stringToBounds,
	IMarker
} from "@sureview/v2-mapping";
import { Component, Vue, Watch, Prop, Ref } from "vue-property-decorator";
import { namespace, Getter, Mutation, State, Action } from "vuex-class";
import { get } from "lodash";
import { darkMapStyle, satelliteMapStyle, getMapStyle } from "@/scripts/mapping/mapStyle";
import { GPSService, GeoCodeResponse, GeoCodeLocation } from "@/services/gps.service";

// Components
import AreaTreeSelect from "@/components/form/AreaTreeSelect.vue";
import EnhancedMap from "@/components/EnhancedMap.vue";
import MapConversion from "@/components/MapConversion.vue";
import MapManagement from "@/components/MapManagement.vue";
import MapLayerSetup from "@/components/map-setup/MapLayerSetup.vue";
import MapItemSetup from "@/components/map-setup/MapItemSetup.vue";
import NavHeader from "@/components/NavHeader.vue";

import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { FeaturesList, UserPermissions } from "@/store/types";
import { MapItemTypeData, MapLayerData, MapItemData } from "@/store/map-layers/types";
import {
	MapSetupGroup,
	MapSetupLayer,
	MapSetupItem,
	LayerMap,
	ItemMap,
	MapLayerRequest,
	Devices,
	MapSetupLayerItemRequest,
	mapItemsUpdated,
  closeInfoWindowDeleteData,
  closeInfoWindowUpdateData
} from "@/store/map-setup/types";
import { GroupWithPermissions } from "@/store/areas/types";
import { mapLayers } from "../store/map-layers";
import { Map } from "@/store/map-setup/types";
import { mapSetup } from '@/store/map-setup';

const Areas = namespace("areas");
const MapLayers = namespace("mapLayers");
const MapSetupStore = namespace("mapSetup");
const geoCoder = new GPSService();

interface ImageSize {
	height: number,
	width: number
}

interface LayerLoadStatus {
	layerID: Number,
	isCurrentlyLoading: Boolean,
	hasInitialised: Boolean
}

class BrokenItemObjectMap {
	BrokenItemIDs: number[];

	constructor() {
		this.BrokenItemIDs = [];
	}
}

@Component({
	components: {
		"area-tree-select": AreaTreeSelect,
		"map-layer-modal": MapLayerSetup,
		"map-setup": EnhancedMap,
		"map-setup-popup": MapItemSetup,
		"map-conversion": MapConversion,
		"nav-header": NavHeader,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"map-management": MapManagement
	}
})
export default class MapSetup extends Vue {
	@Action fetchUserGroups: () => Promise<any>;

	@Areas.Action getArea: (areadId: number) => Promise<GroupWithPermissions>;

	@Getter public getHideLabels: any;
	@Getter public getMapType: any;
	@Getter("getMapKey") private mapKey: string;
	@Getter("getUserGroups") public userGroups;
	@Getter("getCustomMapViewTypes") public customMapViewTypes;
	@Getter getPermissions: UserPermissions;
	@Getter private getFeature: (featureName: string[]) => boolean;

	@MapLayers.Action loadMapLayersByGroupID: (groupId: number) => Promise<MapLayerData[]>;
	@MapLayers.Action loadMapLayerItemsByMapLayer: (mapLayerId: number) => Promise<MapItemData[]>;
	@MapLayers.Action loadMapUnlinkedItemsByGroup: (groupId: number) => Promise<MapItemData[]>;
	@MapLayers.Action public loadMapLayers: (bounds: any) => Promise<MapLayerData[]>;
	@MapLayers.Action public loadMapLayersItemTypes: () => Promise<MapItemTypeData[]>;
	@MapLayers.Action loadPlottedMapLayerItemsByGroupID: (groupID: Number) => Promise<MapItemData[]>;
	@MapLayers.Getter("getMapItemIcons") public mapIcons!: any[];
	@MapLayers.Getter("getBrokenMapItemIcons") public brokenMapIcons!: any[];
	@MapLayers.Getter("getEditableMapLayerItemTypes") public mapItemTypes: MapItemTypeData[];

	@MapSetupStore.Action updateAreaLatLong: ({ groupId: number, latLong: any }) => Promise<void>;
	@MapSetupStore.Action updateMapItem: ({ mapSetupLayer, mapSetupLayerItem }: any) => MapSetupItem;
	@MapSetupStore.Getter("getGroupsWithLayers") groupsWithLayers!: MapSetupGroup[];
	@MapSetupStore.Getter("getSelectedLayerId") selectedMapLayerId!: number | null;
	@MapSetupStore.State unconvertedMaps!: Map[];
	@MapSetupStore.Mutation addMapSetupGroup: (groupWithLayers: MapSetupGroup) => void;
	@MapSetupStore.Mutation addUpdateMapSetupLayer: (layer: MapSetupLayer) => void;
	@MapSetupStore.Mutation addUpdateMapSetupLayerItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => void;
	@MapSetupStore.Mutation removeMapSetupItem: (mapSetupItemRequest: MapSetupLayerItemRequest) => void;
	@MapSetupStore.Mutation resetState: () => void;
	@MapSetupStore.Mutation setSelectedLayerId: (layerId: number | null) => void;
	@MapSetupStore.Mutation setLayerVisibility: ({ mapSetupLayer, visibility }) => void;
	@MapSetupStore.Mutation updateLayerItemCount: ( mapSetupLayer: MapSetupLayer) => void;

	@Mutation public setMapType: any;

	@Ref() mappingContainer: HTMLElement;

	@State featuresList: FeaturesList;

	private AddingLayer: boolean = false;
	private EditingLayer: boolean = false;
	private addressNotFound: boolean = false;
	private addressNotFoundErrorDuration: number = 6000;
	private defaultLocation = { lat: 51.621441, lng: -3.943646 };
	private dimensions = null;
	private drawingManager: google.maps.drawing.DrawingManager = null;
	private enhancedMapping: IEnhancedMapping | null = null;
	private infoWindow: google.maps.InfoWindow = null;
	private layerLoadingDuration: number = 1000;
	private loading: boolean = false;
	private savingMapLayerItems: boolean = false;
	private mapCenter: ILatLng | null = null;
	private mapType: string = "";
	private newMarker: IMarker = null;
	private previewLayer: ILayer = null;
	private rectangle: google.maps.Rectangle = null;
	private polygon: google.maps.Polygon = null;
	private showMapLayers: boolean = true;
	private showMapTools: boolean = true;
	private plottedItems: MapItemData[] = [];

	private rectangleOptions: google.maps.RectangleOptions = {
		editable: true,
		draggable: true,
		strokeColor: "#2196F3",
		fillOpacity: 0
	};

	private polygonOptions: google.maps.PolylineOptions = {
		editable: true,
		draggable: true,
		strokeColor: "#2196F3"
	};


	private layersLoadStatus: LayerLoadStatus[] = [];
	private roofTopZoomLevel: number = 20;
	private selectedAreaId: number = 0;
	private selectedGroup: MapSetupGroup = new MapSetupGroup();
	private selectedItemType: MapItemTypeData;
	private selectedLayer: MapSetupLayer = new MapSetupLayer();
	private setupMap: IMap | null = null;
	private zoomLevel: number = 3;

	public googleMapLayers: LayerMap = new LayerMap();
	public googleMapLayerItems: ItemMap = new ItemMap();

	private divOverlayContainer: HTMLElement = null;
	private rotationContainer: HTMLElement = null;
	private imgLayerImage: HTMLElement = null;
	private rotationContainerCenter: any = null;
	private rotationHandle: HTMLElement = null;
	private mapItemsUpdated: mapItemsUpdated[] = [];
	private brokenMapItems: BrokenItemObjectMap[] = [];
	private showInfoBox: boolean = false;
	private isPlotting: boolean = false;
	private newlyConvertedMapLayer: MapSetupLayer = null;
	private rotateContainer: boolean = true;
	private unlinkedItemsLayers: MapSetupLayer[] = [];
	private nextAvailableUnlinkedItemsLayerID: number = -100;
	private mapToConvert: Map = null;

	private getSetupLayerData(setupLayer: MapSetupLayer): MapLayerData {
		let unlinkedLayerData = new MapLayerData();

		unlinkedLayerData.mapLayerID = setupLayer.mapLayerId;
		unlinkedLayerData.title = setupLayer.title;
		unlinkedLayerData.groupID = setupLayer.groupId;
		unlinkedLayerData.groupTitle = setupLayer.groupTitle;
		unlinkedLayerData.ne = setupLayer.ne;
		unlinkedLayerData.nw = setupLayer.nw;
		unlinkedLayerData.se = setupLayer.se;
		unlinkedLayerData.sw = setupLayer.sw;

		return unlinkedLayerData;
	}


	private initialiseNewUnlinkedLayer(): MapSetupLayer {
		let unlinkedItemsLayer = new MapSetupLayer();

		unlinkedItemsLayer.mapLayerId = this.nextAvailableUnlinkedItemsLayerID;
		unlinkedItemsLayer.visible = false;
		unlinkedItemsLayer.itemCount = 0;
		unlinkedItemsLayer.groupId = this.selectedGroup.groupId;
		unlinkedItemsLayer.groupTitle = this.selectedGroup.title;
		unlinkedItemsLayer.title = "Unlinked Items";
		unlinkedItemsLayer.ne = "0 0";
		unlinkedItemsLayer.nw = "0 0";
		unlinkedItemsLayer.se = "0 0";
		unlinkedItemsLayer.sw = "0 0";
		unlinkedItemsLayer.layerItems = [];
		unlinkedItemsLayer.isUnlinkedItemsLayer = true;

		this.unlinkedItemsLayers.push(unlinkedItemsLayer);
		this.nextAvailableUnlinkedItemsLayerID--;

		return unlinkedItemsLayer;
	}

	private actionNeededPillVariant(layerID: number) {
		// we return "primary" for the layers that aren't the selected layer
		// we want the pill to turn red for the selected layer only 
		if (!layerID) {
			return "primary";
		}
		
		let brokenItemsPresent = this.getLayerBrokenItems(layerID)?.length > 0 && this.showBrokenItemsFeatureEnabled;
		let unsavedItemsPresent = this.mapItemsUpdated?.length > 0;

		return (brokenItemsPresent || unsavedItemsPresent) ? "danger" : "primary";
	}

	public async mounted() {
		if (this.canViewMapSetup) {
			// get list of groups that the user has access to
			await this.fetchUserGroups();

			// load available map item types to plot on map
			await this.loadMapLayersItemTypes();

			// setup map and page
			await this.init();
		}
	}

	/**
	 * Setup function that creates map and event listeners etc..
	 */
	public async init() {
		// create new enhanced maping object
		this.enhancedMapping = await getEnhancedMapping("google", this.mapKey);

		// center the map to our default location
		this.mapCenter = this.defaultLocation;

		// create a new map
		this.setupMap = this.enhancedMapping.createMap(
			this.mappingContainer,
			this.defaultLocation,
			this.zoomLevel,
			false,
			this.getMapType,
			getMapStyle(this.getMapType, this.getHideLabels),
			this.customMapViewTypes
		);

		// set map type, ie Road, Hybrid, Satellite
		this.mapType = this.getMapType;

		// setup click handle to run when the map type changes
		this.setupMap!.registerEvent("onMapTypeChanged", (mapType: string) => {
			this.mapType = mapType;
			this.setMapType(mapType);

			this.setupMap!.map.setOptions({
				styles: getMapStyle(this.getMapType, this.getHideLabels)
			});
		});

		// setup click handler to run when the map is moved or zoomed
		this.setupMap!.registerEvent("onViewportChangedDelayed", this.onViewportChangedDelayed);

		// event handler for changing the map display type
		//this.setupMap!.registerEvent("onDisplayTypeChanged", (data: any) => {});

		// setup even to handle a clist on the map. This is used to plot a new map item
		this.setupMap!.registerEvent("onClick", this.onMapItemPlot);

		this.drawingManager = new google.maps.drawing.DrawingManager({
			drawingMode: google.maps.drawing.OverlayType.RECTANGLE,
			drawingControl: false,

			rectangleOptions: this.rectangleOptions,
			polygonOptions: this.polygonOptions
		});

		// sets up event handler for drawing a new rectangle on the map
		google.maps.event.addListener(this.drawingManager, "rectanglecomplete", this.onRectangleComplete);

		// sets up event handler for drawing a new polygon on the map
		google.maps.event.addListener(this.drawingManager, "polygoncomplete", this.onPolygonComplete);

		google.maps.event.addListener(this.setupMap.map, "dragstart", () => {
			this.hideRotationElements();
		});

		google.maps.event.addListener(this.setupMap.map, "dragend", () => {
			this.setRotationElementDimentions();
		});

		google.maps.event.addListener(this.setupMap.map, "zoom_changed", () => {
			this.hideRotationElements();
		});

		// @ts-ignore (we are adding this)
		if (!google.maps.Polygon.prototype.getBounds) {
			// @ts-ignore
			google.maps.Polygon.prototype.getBounds = function() {
				let bounds = new google.maps.LatLngBounds();
				this.getPath().forEach(function(element) {
					bounds.extend(element);
				});
				return bounds;
			};
		}
	}

	/**
	 * Event handler to run on plot of a new map item on the map
	 *
	 * @param {any} latLong - Coordinates of the item on the map
	 */
	private onMapItemPlot(latLong: any) {
		// only show item setup screen if we have group selected
		if (this.selectedGroup && this.selectedItemType && this.isPlotting) {
			this.isPlotting = false;

			// create new map item
			let newItem: MapSetupItem = {
				mapLayerItemTypeID: this.selectedItemType.mapLayerItemTypeID,
				title: "",
				latLong: `${latLong.lat} ${latLong.lng}`,
				mapLayerItemID: 0,
				minElevation: this.selectedLayer.minElevation ?? 0,
				maxElevation: this.selectedLayer.maxElevation ?? 0,
				objectID: 0,
				extraValue: null,
				regionPath: null,
				mapLayerIds: [],
				saved: true,
				locationIdentifiers: [],
				brokenObjectLink: false
			};

			this.newMarker = this.enhancedMapping.createMarker(
				this.setupMap!, // Enhanced Mapping
				newItem.mapLayerItemID, // MapLayer Item Id
				null, // Event Record Id
				newItem.mapLayerItemTypeID, // Map Item Type Id
				newItem.objectID, // Object Id
				newItem.title, // Title
				convertStringToLatLng(newItem.latLong!), // Position
				newItem.regionPath, // Region Path
				newItem.minElevation, // Min Elevation
				newItem.maxElevation, // Max Elevation
				true // Visible
			);

			// set defaults for the new map item
			this.selectedLayer.groupId = this.selectedGroup.groupId;
			this.selectedLayer.groupTitle = this.selectedGroup.title;
			this.setSelectedLayerBounds(this.setupMap.mapBounds);

			this.setupMap!.setCursor("grab");

			if (this.rectangle) {
				this.rectangle.setVisible(false);
			}

			// open a new window popup for creation of the new map item
			this.openWindow(newItem);
		}
	}

	/**
	 * Retrieves the feature flag setting for whether or not we are displaying broken map items.
	 */
	private get showBrokenItemsFeatureEnabled(): boolean {
		return this.getFeature(["MapSetup", "ShowBrokenMapItems"]);
	}

	/**
	 * Permission and feature check
	 */
	public get canViewMapSetup(): boolean {
		return get(this.featuresList, ["MapSetup"]) && (this.getPermissions.isAdmin || this.getPermissions.canEditMaps);
	}

	/**
	 * Feature flag check
	 */
	public get canConvertMaps(): boolean {
		return get(this.featuresList, ["MapSetup", "MapConversion"], false) && (this.getPermissions.isAdmin || this.getPermissions.canEditMaps);
	}

	/**
	 * Feature flag and permission check for managing maps
	 */
	private get canManageMaps(): boolean {
		return get(this.featuresList, ["MapSetup", "MapManagement"], false) && (this.getPermissions.isAdmin || this.getPermissions.canEditMaps);
	}

	/**
	 * On change of selectedAreaId (triggered by <area-select>), get the address for the area, and reposition the map
	 *
	 * @param {number} groupId - groupId of the area that has been selected
	 */
	@Watch("selectedAreaId")
	private async onSelectedAreaIdUpdated(groupId: number) {
		if (groupId && groupId > 0 && this.selectedGroup.groupId != groupId) {
			this.closeLayerSetup();
			this.clearMap();
			this.loading = true;

			// check the existing data to see if we already have this group
			let group = this.groupsWithLayers.find((g: MapSetupGroup) => g.groupId == groupId);

			if (!group) {
				// make API request to get the data
				let result = await this.getArea(groupId);

				group = new MapSetupGroup(result);

				this.addMapSetupGroup(group);
			}
			// update our object with the new data

			this.selectedGroup = group;
		}
	}
	/**
	 * Runs when the selected group is changed, finds the correct location and repositions the map
	 *
	 * @param {MapSetupGroup} group MapSetupGroup Object containing the group and layer data
	 */
	@Watch("selectedGroup")
	private async onSelectedGroupChanged(group: MapSetupGroup) {
		if (group) {
			// reset current selection
			this.selectedAreaId = null;
			let areaLatLong = null;

			// if the group already has a lat long, use that
			if (group.latLong && group.latLong.length > 0) {
				areaLatLong = convertStringToLatLng(group.latLong);

				this.goToLocation(this.roofTopZoomLevel, areaLatLong);
			}

			// otherwise if we have an address, do a GeoLookup to get its coordinates
			else if (this.selectedGroup && this.selectedGroup.address && this.selectedGroup.address.length > 0) {
				// request to google to get coordinates from address
				let response = await geoCoder.GeoCodeLookup(this.selectedGroup.address, this.mapKey);

				// if we get a response and it has results
				if (response && response.results) {
					// take the first result
					let areaDetails = response.results[0];
					// check our result has the data we need
					if (areaDetails && areaDetails.geometry && areaDetails.geometry.location) {
						let location = areaDetails.geometry.location;
						// if we successfully get the location, reposition the map and zoom to roof

						await this.updateAreaLatLong({ groupId: this.selectedGroup.groupId, latLong: location });

						this.goToLocation(this.roofTopZoomLevel, location);
					}
				}
				// if no resultsa are found display an error message
				if (response && response.status == "ZERO_RESULTS") {
					this.showNoAddressError();
				}
			}

			// begin loading layer and their items for the selected group
			await this.loadLayersAndItems(this.selectedGroup.groupId);
		}
	}

	/**
	 * Sets the selected group object
	 */
	private async setSelectedGroup(group: MapSetupGroup): Promise<void> {
		this.selectedGroup = group;
		this.selectedAreaId = null;
	}

	/**
	 * move to the map to the givem position and zoom level
	 *
	 * @param {number} zoom - zoom level to set the map to
	 * @param {GeoCodeLocation} location - LatLong coordinates to center the map on
	 */
	private goToLocation(zoom: number = 10, location: GeoCodeLocation): void {
		if (this.setupMap && location && location.lat && location.lng) {
			this.setupMap.map.zoom = zoom;
			this.setupMap.map.setCenter({ lat: location.lat, lng: location.lng });
		}
	}


	private async loadLayerLinkedOrUnlinkedItems(mapLayer: MapSetupLayer): Promise<MapItemData[]> {
		let items: MapItemData[] = [];

		if (mapLayer.isUnlinkedItemsLayer) {
			// make request to get the unlinked map items for this group.
			items = await this.loadMapUnlinkedItemsByGroup(mapLayer.groupId);
			items.forEach(item => {
					if (!item.mapLayerIds.includes(mapLayer.mapLayerId)) {
						item.mapLayerIds.push(mapLayer.mapLayerId);
					}
				});
		}
		else {
			// make request to get the map items for the layer
			items = await this.loadMapLayerItemsByMapLayer(mapLayer.mapLayerId);
		}

		return items;
	}

	private async loadLayerItems(mapLayer: MapSetupLayer, allLayers: MapSetupLayer[] = null) {
		
		this.layersLoadStatus[mapLayer.mapLayerId].isCurrentlyLoading = true;

		let items: MapItemData[] = await this.loadLayerLinkedOrUnlinkedItems(mapLayer);

		if (this.showBrokenItemsFeatureEnabled) {
			// Get ALL broken items for this layer and merge them into the list of items. List of items is based on screen boundary.
			let brokenItemsForThisLayer = this.plottedItems.filter(item => item.mapLayerIds.includes(mapLayer.mapLayerId));
			
			items = items.filter(itemToFilter => {
				return !brokenItemsForThisLayer.some( matchedItem => {
					return matchedItem.objectID == itemToFilter.objectID;
				});
			});
			items = items.concat(brokenItemsForThisLayer);
		}
		

		let newMapLayer: MapSetupLayer = new MapSetupLayer();
		
		newMapLayer.mapLayerId = mapLayer.mapLayerId;
		newMapLayer.groupId = mapLayer.groupId;
		newMapLayer.itemCount = items.length;

		this.updateLayerItemCount(newMapLayer);

		// create google map item objects - this "CAN" take up some time on slow machines
		await this.displayItems(items, mapLayer, allLayers);

		this.layersLoadStatus[mapLayer.mapLayerId].isCurrentlyLoading = false;

		// Set initialisation for this layer as complete.
		this.layersLoadStatus[mapLayer.mapLayerId].hasInitialised = true;
		
	}


	/**
	 * Show Address not found error
	 */
	private showNoAddressError(): void {
		this.addressNotFound = true;
		setTimeout(() => {
			this.addressNotFound = false;
		}, this.addressNotFoundErrorDuration);
	}

	/**
	 * Loads all available map layers and their items for the given group
	 *
	 * @param {number} groupId - groupId to load layers for
	 */
	private async loadLayersAndItems(groupId: number): Promise<void> {
		this.loading = true;

		// see if we already have an object for this group
		let group: MapSetupGroup = this.groupsWithLayers.find((g: MapSetupGroup) => g.groupId == groupId);

		// if not create a new one and add it to the array
		if (!group && this.userGroups) {
			let newGroup = this.userGroups.find(g => g.groupID == groupId);

			group = new MapSetupGroup(newGroup);

			this.addMapSetupGroup(group);
		}

		let unlinkedLayer: MapSetupLayer = undefined;

		// Create a new layer responsible for holding all unlinked items.
		if (!group.mapLayers.some(layer => this.unlinkedItemsLayers.some(unlinkedItemLayer => 
			unlinkedItemLayer.mapLayerId === layer.mapLayerId))
		) {
			unlinkedLayer = this.initialiseNewUnlinkedLayer();
			this.addUpdateMapSetupLayer(unlinkedLayer);
		}


		// begin loading layers
		let layers: MapLayerData[] = await this.loadMapLayersByGroupID(groupId);

		// create google map overlays for the map
		await this.displayLayers(group, unlinkedLayer ? [...layers, this.getSetupLayerData(unlinkedLayer)] : layers, false);
		
		if (this.showBrokenItemsFeatureEnabled) {
			this.plottedItems = await this.loadPlottedMapLayerItemsByGroupID(groupId);
		}
		
		if (group.mapLayers && group.mapLayers.length > 0) {
	
			// Setup every layer
			group.mapLayers.forEach(mapLayer => {
				/* create a reference of the layer and its' status in the layersLoadStatus indexed by the layer ID.
				layerID: <LayersID>,
				isCurrentlyLoading: true,
				hasInitialised: false
				*/
				this.layersLoadStatus[mapLayer.mapLayerId] = { layerID: mapLayer.mapLayerId, isCurrentlyLoading: false, hasInitialised: false};
				// Begin loading the layer, the details above are updating throughout this process.
				this.loadLayerItems(mapLayer, group.mapLayers);
			});

			//this.setLoadingComplete();

			// Interval checking to see if every layer has finished initialisation, if it has; set loading to false(complete) display the first layer on screen and destroy the interval.
			let checkInitialisedInterval = setInterval(() => {
				let hasFullyInitialised = true;
				this.layersLoadStatus.forEach(item => {
					if (!item.hasInitialised) {
						hasFullyInitialised = false;
					}
				});

				if (hasFullyInitialised) {
					this.setLoadingComplete();
					if (group.mapLayers.length > 1) {
						this.gotoLayer(group.mapLayers[1]);
					}
					else {
						this.gotoLayer(group.mapLayers[0]);
					}
					clearInterval(checkInitialisedInterval);
				}

			}, 50);
		}
		else
		{
			this.setLoadingComplete();
		}
	}

	/**
	 * Sets the loading flag to false.
	 */
	private setLoadingComplete(): void {
		this.loading = false;
	}

	/**
	 * @returns {boolean} Value indicating whether or not there are unsaved changes on the layer.
	 */
	private get layerHasUnsavedChanges(): boolean {
		return this.mapItemsUpdated && this.mapItemsUpdated.length > 0;
	}

	/**
	 * @returns {string} A HTML class
	 */
	private get layerBrokenMapItemsHTMLClass(): string {
		return `card map-info-box broken-items ${this.layerHasUnsavedChanges ? "unsaved-items-offset" : ""} `;
	}

	/**
	 * Method determining if the layer should be inaccessible.
	 * Layer should be inaccessible if initialisation is incomplete or if the layer is currently loading.
	 * @param {MapSetupLayer} layer The layer that needs to be checked.
	 * @returns {Boolean} true if the layer is loading or initialisation is incomplete.
	 */
	private shouldLayerBeDisabled(layer: MapSetupLayer): Boolean {
		if (this.layersLoadStatus[layer.mapLayerId])
		{
			return (this.isLayerLoading(layer) || this.loading) ? true : false
		}

		return true;
	}

	/**
	 * Gets an ID list of all the broken items on the provided layer.
	 * @param {number} layerID The ID of the layer we are checking for broken items.
	 * @returns {number[]} list of broken item ID's.
	 */
	private getLayerBrokenItems(layerID: number): number[] {
		return this.brokenMapItems[layerID]?.BrokenItemIDs ?? [];
	}

	/**
	 * Method determining if the layer is currently loading.
	 * @param {MapSetupLayer} layer The layer that needs to be checked.
	 * @returns {Boolean} Whether or not the layer is currently loading.
	 */
	private isLayerLoading(layer: MapSetupLayer): Boolean {
		if (this.layersLoadStatus[layer.mapLayerId])
		{
			return this.layersLoadStatus[layer.mapLayerId].isCurrentlyLoading
		}

		return true;
	}


	/**
	 * Method determining if the layer is currently initialising
	 * @param {MapSetupLayer} layer The layer that needs to be checked.
	 * @returns {Boolean} Whether or not the layer is being initialised 
	 */
	private isLayerInitialising(layer: MapSetupLayer): Boolean {
		if (this.layersLoadStatus[layer.mapLayerId])
		{
			return (!this.layersLoadStatus[layer.mapLayerId].hasInitialised && this.layersLoadStatus[layer.mapLayerId].isCurrentlyLoading);
		}

		return true;
	}

	/**
	 * Method determining if the layer has been initialised 
	 * @param {MapSetupLayer} layer The layer that needs to be checked.
	 * @returns {Boolean} Whether or not the layer HAS been initialised 
	 */
	private isLayerInitialised(layer: MapSetupLayer): Boolean {
		if (this.layersLoadStatus[layer.mapLayerId])
		{
			return this.layersLoadStatus[layer.mapLayerId].hasInitialised;
		}

		return false;
	}

	/**
	 * Create google map objects for each layer and adds them to the given group object
	 *
	 * @param {MapSetupGroup} group MapSetupGroup object
	 * @param {MapLayerData[]} group MapLayerData object array
	 * @param {Boolean} shouldToggleLoadingState - Whether or not this method should toggle this.loading to false.
	 */
	private async displayLayers(group: MapSetupGroup, layers: MapLayerData[], shouldToggleLoadingState: Boolean = true): Promise<void> {
		layers.forEach(layer => {
			// if the group does not already contain this layer, create and add it
			if (!group.mapLayers.some((e: MapSetupLayer) => e.mapLayerId === layer.mapLayerID)) {
				// create new google map overlay
				let googleMapLayer = this.createOverlay(layer);

				// hide the overlay, it will become visible when the layer is selected
				googleMapLayer.visible = false;

				// keep a reference of the created google map layer
				this.googleMapLayers[layer.mapLayerID] = googleMapLayer;

				// create a new map setup layer for our layer data
				let newLayer = new MapSetupLayer(layer);

				// add it to the list of layers for the group
				this.addUpdateMapSetupLayer(newLayer);
			}
		});

		if (shouldToggleLoadingState)
		{
			this.loading = false;
		}
	}

	/**
	 * Create google map objects for each map item and adds them to the given layer object
	 *
	 * @param {MapItemData[]} mapItems - MapItemData Object Array of items to display
	 * @param {MapSetupLayer} mapSetupLayer - MapSetupLayer Object for the layer linked to the items
	 */
	public async displayItems(mapItems: MapItemData[], mapSetupLayer: MapSetupLayer, allLayers: MapSetupLayer[] = null): Promise<void> {
		this.layersLoadStatus[mapSetupLayer.mapLayerId].isCurrentlyLoading = true;
		
		if (this.brokenMapItems[mapSetupLayer.mapLayerId] == null) {
			this.brokenMapItems[mapSetupLayer.mapLayerId] = new BrokenItemObjectMap();
		}

		mapItems.forEach((mapItem: MapItemData) => {
			// create new google map marker (The create will also update if needed)
			if (allLayers) {
				allLayers.forEach(mapLayer => {
					if (mapItem.mapLayerIds && mapItem.mapLayerIds.includes(mapLayer.mapLayerId)) {
						this.createMarker(mapItem, mapLayer);
					}
				})
			}
			else {
				this.createMarker(mapItem, mapSetupLayer);
			}
		});

		this.layersLoadStatus[mapSetupLayer.mapLayerId].isCurrentlyLoading = false;
	}

	/**
	 * Helper method for creating a marker
	 */
	private createMarker(mapItem: MapItemData | MapSetupItem, mapSetupLayer: MapSetupLayer): IMarker {
		let mapMarkerItem = this.enhancedMapping.createMarker(
			this.setupMap!,
			mapItem.mapLayerItemID,
			null,
			mapItem.mapLayerItemTypeID,
			mapItem.objectID,
			`${mapItem.mapLayerItemID}-${mapItem.title}`, // adds the map item id to the title so we know which item is it
			convertStringToLatLng(mapItem.latLong!),
			mapItem.regionPath,
			mapItem.minElevation,
			mapItem.maxElevation,
			true,
			mapItem.locationIdentifiers
		);

		// set it to hidden, it becomes visible when the layer is selected
		mapMarkerItem.visible = false;

		let vueThis = this;

		if (mapMarkerItem && mapMarkerItem.marker) {
			// cant use arrow function here () => as I need the scope of `this` to be from within the function
			mapMarkerItem.marker.addListener("dragend", function(event: google.maps.MouseEvent) {
				vueThis.updateMarkerItemPosition(event.latLng, this.title); // this = the marker clicked on not the vue instance
			});
		}

		// create a new map setup item for our item data, and a reference to the google marker object
		let mapSetupLayerItem = mapItem as MapSetupItem;
		this.googleMapLayerItems[mapSetupLayerItem.mapLayerItemID] = mapMarkerItem;

		// add it to the list of items for the layer
		this.addUpdateMapSetupLayerItem({ mapSetupLayers: [mapSetupLayer], mapSetupLayerItem } as MapSetupLayerItemRequest);

		// setup click handler for the markers, this will be display the item setup window

		mapMarkerItem.state = (mapItem.brokenObjectLink && this.showBrokenItemsFeatureEnabled) ? "broken" : "active";
		if (mapItem.brokenObjectLink && this.showBrokenItemsFeatureEnabled) {
			// add this item to the array so we can track that this marker represents a broken item.
			if (!this.brokenMapItems[mapSetupLayer.mapLayerId].BrokenItemIDs.includes(mapItem.mapLayerItemID)) {
				this.brokenMapItems[mapSetupLayer.mapLayerId].BrokenItemIDs.push(mapItem.mapLayerItemID);
			}
		}
		mapMarkerItem.isClickable = true;
		mapMarkerItem.click = () => {
			this.openWindow(mapItem);
		};

		return mapMarkerItem;
	}

	/**
	 * Event handler for updating a marker position when it is moved
	 *
	 * @param {ILatLng} latlong
	 * @param {string} title
	 */
	private updateMarkerItemPosition(latLong: ILatLng, title: string) {
		if (latLong && title) {
			// the map layerId has been added to the title so we can extract it here (no other way of identifying what marker has been clicked on)
			let data = title.split("-");
			let itemId = Number.parseInt(data[0]);

			// find the existing item
			let currentItem = this.selectedLayer.layerItems.find(f => f.mapLayerItemID == itemId);

			if (currentItem) {
				let updateItem : mapItemsUpdated = {layerId: this.selectedLayer.mapLayerId, itemId: itemId, oldLatLong: currentItem.latLong }

				// update the lat and long
				currentItem.latLong = `${latLong.lat()} ${latLong.lng()}`;

				// add update item to the list if not already there
				let existingUpdateItem = this.mapItemsUpdated.find(f => f.itemId == itemId);
				if (existingUpdateItem == null) {
					this.mapItemsUpdated.push(updateItem);
				}

				
			}
		}
	}

	/**
	 * Creates a new EnhancedMap overlay
	 *
	 * @param {MapSetupLayer|MapLayerData} layer
	 * @param {ILatLngBounds} bounds
	 * @param {string} image
	 */
	private createOverlay(
		layer: MapSetupLayer | MapLayerData,
		bounds: ILatLngBounds = null,
		image: string = null
	): ILayer {
		// take the given layer and convert it to a map setup layer
		let newlayer = new MapSetupLayer(layer);

		return this.enhancedMapping.createOverlay(
			this.setupMap!,
			newlayer.mapLayerId,
			newlayer.title,
			bounds ?? stringToBounds(newlayer.ne, newlayer.sw), // boundaries
			newlayer.rotation, // rotation
			image ?? newlayer.image, // imageUrl
			newlayer.minElevation, // minElevation,
			newlayer.maxElevation, // maxElevation
			true // , visible by default or it would not get created correctly
		);
	}

	/**
	 * Create a kebab case string, used for ids and css selectors
	 *
	 * @param {string} title
	 */
	private getTitle(title: string): string {
		if (title) return title.replace(" ", "-");
	}

	/**
	 * Returns the map icon for the given item ID and Type ID
	 *
	 * @param {number} mapItemID
	 * @param {number} mapItemTypeID
	 */
	private getMapItemIcon(mapItemID: number, mapItemTypeID: number): string {
		if (this.showBrokenItemsFeatureEnabled && this.brokenMapItems[this.selectedLayer.mapLayerId]?.BrokenItemIDs.includes(mapItemID)) {
			return this.brokenMapIcons[mapItemTypeID];
		}
		return this.mapIcons[mapItemTypeID];
	}

	/**
	 * Moves the map to the selected layer, sets up the layer and UI for editing
	 *
	 * @param {MapSetupLayer} layer
	 */
	public async gotoLayer(layer: MapSetupLayer, editOverLay: boolean = false): Promise<void> {
		if ((layer && this.selectedMapLayerId != layer.mapLayerId) || editOverLay) {
			this.layersLoadStatus[layer.mapLayerId].isCurrentlyLoading = true;

			// hide all layers and items for the current group
			this.closeLayerSetup();

			// set the selected group to the one linked to this layer
			this.selectedGroup = this.groupsWithLayers.find((f: MapSetupGroup) => f.groupId == layer.groupId);

			// set selected layer id, used for css classes
			this.setSelectedLayerId(layer.mapLayerId);

			let items: MapItemData[] = await this.loadLayerLinkedOrUnlinkedItems(layer);

			// create google map item objects
			this.displayItems(items, layer);

			// trigger edit layer state
			this.editLayer(layer, editOverLay);
		}
	}

	/**
	 * Setups up data and sets UI to edit mode for editing the selected layer
	 *
	 * @param {MapSetupLayer} layer
	 */
	private editLayer(layer: MapSetupLayer, editOverLay: boolean = false): void {
		// reset edit status
		this.EditingLayer = false;

		// reset the data on the selected layer object
		if (this.selectedLayer) {
			this.selectedLayer.reset();
		}

		this.showInfoBox = !editOverLay;

		// TODO : remove time out, with out it, edit popup doesnt trigger
		setTimeout(async () => {
			// set the layer we are going to edit
			this.selectedLayer = new MapSetupLayer(layer);

			this.setSelectedLayerId(this.selectedLayer.mapLayerId);

			// if the overlay for the layer doesnt exist, create (this will be the case if we have just created a new one)
			if (!this.googleMapLayers[this.selectedLayer.mapLayerId]) {
				let googleMapLayer = this.createOverlay(layer);

				// keep a reference of the created google map layer
				this.googleMapLayers[this.selectedLayer.mapLayerId] = googleMapLayer;
			}

			if (editOverLay) {
				// set editing status to true
				this.EditingLayer = true;

				// create a new rectangle to allow repositioning and sizing of the layer image
				this.rectangle = new google.maps.Rectangle({
					map: this.setupMap.map
				});

				this.rectangle.setBounds(this.googleMapLayers[this.selectedLayer.mapLayerId].bounds);

				this.rectangle.setOptions(this.rectangleOptions);

				// create an overlay of the image
				this.previewLayerImageChange(this.selectedLayer.image);

				// hook up the event listener to respond to changes to the rectangle
				this.rectangle.addListener("bounds_changed", this.onRectangleBoundsChanged);
			} else {
				await this.toggleMapLayerImage(this.selectedLayer.mapLayerId, true);
			}

			// make all items visible for this layer
			this.selectedLayer.layerItems.forEach((item: MapSetupItem) => {
				let marker: IMarker = this.googleMapLayerItems[item.mapLayerItemID];

				if (marker) {
					marker.visible = true;
					marker.isDraggable = true;
				}
			});
		}, 500);
	}

	/**
	 * Hides the rotation UI
	 */
	private hideRotationElements() {
		if (this.rotationContainer) {
			this.rotationContainer.style.visibility = "hidden";
		}
	}

	/**
	 * Sets the dimentions and size of the rotate element for rotating a map layer image
	 */
	private setRotationElementDimentions() {
		// find over lay div
		this.divOverlayContainer = document.getElementById("divOverlayContainer");
		this.imgLayerImage = document.getElementById("imgLayerImage");

		if (this.divOverlayContainer && this.EditingLayer) {
			// find rotation UI elements
			this.rotationContainer = document.getElementById("rotation-container");

			this.rotationHandle = document.getElementById("rotation-handle");
			var rect = this.divOverlayContainer.getBoundingClientRect();

			// workout top / left pixels for the rotation container
			this.rotationContainerCenter = {
				x: rect.left + rect.width / 2,
				y: rect.top + rect.height / 2
			};

			// set dimentions based on the parent attributes
			this.rotationContainer.style.top = this.rotationContainerCenter.y + "px";
			this.rotationContainer.style.left = this.rotationContainerCenter.x + "px";
			this.rotationContainer.style.width = parseInt(this.divOverlayContainer.style.width) + "px";
			this.rotationContainer.style.visibility = "visible";
		}
	}

	/**
	 * Hooks up the relevent event handlers for rotating the map layer image
	 */
	private loadRotationElement() {
		this.setRotationElementDimentions();
		// workout with transform method can be used based on what is available to the browser
		let transform = (() => {
			let prefs = ["t", "WebkitT", "MozT", "msT", "OT"],
				style = document.documentElement.style,
				p;
			for (let i = 0, len = prefs.length; i < len; i++) {
				if ((p = prefs[i] + "ransform") in style) return p;
			}

			console.error("your browser doesnt support css transforms!");
		})();

		// funcation to calculate rotation angle for the image based off where the mouse is
		let rotate = (x, y) => {
			let deltaX = x - this.rotationContainerCenter.x;
			let deltaY = y - this.rotationContainerCenter.y;

			let angle = +((Math.atan2(deltaY, deltaX) * 180) / Math.PI).toFixed();

			// store the value for our layer
			this.selectedLayer.rotation = angle;

			return angle;
		};

		// DRAGSTART
		let mousedown = event => {
			event.preventDefault();
			document.body.style.cursor = "move";
			mousemove(event);
			document.addEventListener("mousemove", mousemove);
			document.addEventListener("mouseup", mouseup);
		};

		// DRAG
		let mousemove = event => {
			this.rotationContainer.style[transform] = "rotate(" + rotate(event.pageX, event.pageY) + "deg)";
			if (this.rotateContainer) {
				this.divOverlayContainer.style[transform] = "rotate(" + rotate(event.pageX, event.pageY) + "deg)";
			} else {
				this.imgLayerImage.style.transform = "rotate(" + rotate(event.pageX, event.pageY) + "deg)";
			}
		};

		// DRAGEND
		let mouseup = () => {
			document.body.style.cursor = null;
			document.removeEventListener("mouseup", mouseup);
			document.removeEventListener("mousemove", mousemove);
		};

		if (!this.rotationHandle) {
			this.rotationHandle = document.getElementById("rotation-handle");
		}

		// DRAG START
		this.rotationHandle.addEventListener("mousedown", mousedown);
	}

	/**
	 * Setups up data and sets UI to add mode for creating a new layer
	 */
	private addLayer(): void {
		// reset edit status
		this.EditingLayer = false;

		// reset adding status
		this.AddingLayer = true;

		// reset data ready for a new layer
		this.selectedLayer.reset();

		// remove all items and layers from the map
		this.closeLayerSetup();

		// enables drawing mode to plotting a new layer
		this.drawingManager.setMap(this.setupMap.map);

		// display Layer Setup interfance
		this.EditingLayer = true;

		this.showInfoBox = true;
	}

	/**
	 * Closes the map item setup window
	 */
	private closeInfoWindow(data = undefined): void {
		this.isPlotting = false;

		if (this.infoWindow && typeof this.infoWindow.close == "function") {
			this.infoWindow.close();
		}

		google.maps.event.trigger(this.infoWindow, "closeclick");

		let deleteData = data as closeInfoWindowDeleteData;
		let updateData = data as closeInfoWindowUpdateData;

		// if a delete event triggered this close
		if (deleteData && deleteData.deletedItemId != null){
			if(this.googleMapLayerItems[data.deletedItemId]) {
				this.googleMapLayerItems[data.deletedItemId].visible = false;
				this.googleMapLayerItems[data.deletedItemId].remove();
			}
			let removeItem = this.selectedLayer.layerItems.find(i => i.mapLayerItemID == data.deletedItemId);
			if(removeItem != null){
				this.selectedLayer.layerItems.remove(removeItem);
			}
		}

		// if a save or update event triggered this close
		if (updateData && updateData.updatedItemId != null) {
			if (this.newMarker) {
				this.newMarker.remove();
			}
			
			if (data.removedLayerIds.length > 0 && data.removedLayerIds.includes(this.selectedLayer.mapLayerId)) {
				if (this.googleMapLayerItems[data.updatedItemId]) {
					this.googleMapLayerItems[data.updatedItemId].visible = false;
					this.googleMapLayerItems[data.updatedItemId].remove();
				}
				let removeItem = this.selectedLayer.layerItems.find(i => i.mapLayerItemID == data.deletedItemId);
				if(removeItem != null){
					this.selectedLayer.layerItems.remove(removeItem);
				}
			}

			
			// create the new marker on the map, that was just plotted
			let layer = this.findMapLayerById(this.selectedLayer.mapLayerId);
			let item = layer.layerItems.find(f => f.mapLayerItemID == data.updatedItemId);

			if (item && layer) {
				let marker = this.createMarker(item, layer);
				marker.isDraggable = true;
				marker.visible = true;
				this.selectedLayer.layerItems.push(item);
			}
		}
	}

	/**
	 * Helper method to find a map layer by its Id in the GroupWithLayers Object
	 *
	 * @param {number} mapLayerId
	 */
	private findMapLayerById(maplayerId: number): MapSetupLayer {
		let layer = null;
		let groupId = this.selectedLayer.groupId ?? this.selectedGroup.groupId;
		let group = this.groupsWithLayers.find((f: MapSetupGroup) => f.groupId == groupId);
		if (group) {
			layer = group.mapLayers.find((f: MapSetupLayer) => f.mapLayerId == maplayerId);
		}
		return layer;
	}

	/**
	 * Helper method to find a map layer item by its Id in the GroupWithLayers Object
	 *
	 * @param {number} mapLayerItemId
	 */
	private findMapItemById(mapLayerItemId: number): MapSetupItem {
		let item: MapSetupItem = null;

		let layer = this.findMapLayerById(this.selectedMapLayerId);

		item = layer.layerItems.find((item: MapSetupItem) => item.mapLayerItemID == mapLayerItemId);
		return item;
	}

	/**
	 * Opens a new instance of the Map Item Setup window
	 *
	 * @param {MapSetupItem} mapItem
	 */
	private openWindow(mapItem: MapSetupItem): void {
		if (this.rectangle) {
			this.rectangle.setVisible(false);
		}
		// close any open windows
		this.closeInfoWindow();

		// conver the latlong to an object
		let latLong = convertStringToLatLng(mapItem.latLong);

		let zoom = this.setupMap.map.zoom > this.roofTopZoomLevel ? this.setupMap.map.zoom : this.roofTopZoomLevel;

		// reposition map to the location of the map item
		this.goToLocation(zoom, latLong);

		if (this.selectedLayer.groupId == 0 && this.groupsWithLayers.length > 0) {
			let layers: MapSetupLayer[] = [];

			this.groupsWithLayers.forEach((group: MapSetupGroup) => {
				group.mapLayers.forEach((mapLayers: MapSetupLayer) => {
					layers.push(mapLayers);
				});
			});

			let assignedLayer: MapSetupLayer = layers.find((layer: MapSetupLayer) => {
				return layer.layerItems.some(s => s.mapLayerItemID == mapItem.mapLayerItemID);
			});

			if (assignedLayer) {
				this.selectedLayer = assignedLayer;
			}
		}

		// Create an instance of the popup component to load into the google maps info window
		let mapItemPopup = Vue.extend(MapItemSetup);
		const MyComponent = new mapItemPopup({
			store: this.$store,
			propsData: {
				MapItem: mapItem,
				MapLayer: this.selectedLayer,
				MapLayersForGroup: this.selectedGroup.mapLayers,
				PlottedItems: this.plottedItems,
				UnlinkedItemsLayers: this.unlinkedItemsLayers
			}
		}).$mount();

		// hook up event handlers for emits
		MyComponent.$on("mapItemPopupClosed", (data: any) => this.closeInfoWindow(data));
		MyComponent.$on("setRegion", (data: any) => this.setRegion(data));

		this.infoWindow = new google.maps.InfoWindow({
			content: MyComponent.$el,
			position: latLong,
			pixelOffset: new google.maps.Size(0, -48)
		});

		// event handler for clicking the X in the popup window
		this.infoWindow.addListener("closeclick", () => {
			if (this.googleMapLayerItems[mapItem.mapLayerItemID]) {
				this.googleMapLayerItems[mapItem.mapLayerItemID].isDraggable = true;
			}
			if (this.newMarker) {
				this.newMarker.remove();
			}
			if (this.rectangle) {
				this.rectangle.setVisible(true);
			}

			this.setRotationElementDimentions();
		});

		// make the current marker undraggable while the popup window is open
		if (this.googleMapLayerItems[mapItem.mapLayerItemID]) {
			this.googleMapLayerItems[mapItem.mapLayerItemID].isDraggable = false;
			this.googleMapLayerItems[mapItem.mapLayerItemID].marker.setZIndex(9999999);
		}

		this.infoWindow.open(this.setupMap.map);

		// reset the cursor for the map (changed when plotting a new item)
		this.setupMap!.setCursor("grab");
	}

	/**
	 * Set the map to allow plotting of an item, which will trigget the "onClick" event for the map
	 *
	 * @param {MapItemTypeData} itemType
	 */
	private beginPlottingItem(itemType: MapItemTypeData): void {
		// only allow items to be plotted if we are not currently editing or creatinga layer
		if (this.setupMap && !this.AddingLayer) {
			this.isPlotting = true;
			this.selectedItemType = itemType;

			this.setupMap!.setCursor("crosshair");

			if (this.rectangle) {
				this.rectangle.setVisible(false);
			}

			this.hideRotationElements();
		}
	}

	/**
	 * Event handler for drawing a new rectanlge on the map
	 *
	 * @param {google.maps.Rectangle} rectangle
	 */
	private onRectangleComplete(rectangle: google.maps.Rectangle): void {
		// turn off drawing mode
		this.drawingManager.setMap(null);

		// updates our rectangle object with the properites on the newly create one
		this.rectangle = rectangle;

		// update the width and high display, based on the size of the rectangle
		this.updateDimentions(rectangle);

		// add an event listener to run if the size, shape or position of the rectangle is changed
		this.rectangle.addListener("bounds_changed", this.onRectangleBoundsChanged);

		// create an event handler for clicking on the rectangle, this will trigger a rotation of the map layer
		google.maps.event.addListener(this.rectangle, "click", () => {
			// if we have a preview layer, ie an image has been uploaded
			if (this.previewLayer) {
				// on each click, increase the rotation by 10
				this.previewLayer.rotation = this.previewLayer.rotation + 10;
				this.previewLayer.rotationValue = this.previewLayer.rotationValue + 10;
				this.selectedLayer.rotation = this.previewLayer.rotationValue;
			}
		});

		if (this.selectedGroup) {
			// update the selected layer properties
			this.selectedLayer.groupId = this.selectedGroup.groupId;
			this.selectedLayer.groupTitle = this.selectedGroup.title;

			// update the bounds of the selected layer
			this.setSelectedLayerBounds();

			// display Layer Setup interfance
			this.EditingLayer = true;
		}
	}

	/**
	 * Event handler for drawing a new polygon on the map
	 *
	 * @param {google.maps.Polygon} polygon
	 */
	private onPolygonComplete(polygon: google.maps.Polygon): void {
		// turn off drawing mode
		this.drawingManager.setMap(null);

		// updates our rectangle object with the properites on the newly create one
		this.polygon = polygon;
	}

	/**
	 * Click handler that runs when the size, shape or position of the rectangle is changed
	 */
	private onRectangleBoundsChanged(): void {
		this.updateDimentions(this.rectangle);

		// update the bounds of the selected layer to match
		this.setSelectedLayerBounds();

		if (this.previewLayer) {
			// update the position of the preview layer to match the rectangle
			this.previewLayer.bounds = this.rectangle.getBounds();

			setTimeout(() => {
				this.setRotationElementDimentions();
			}, 1);
		}
	}

	/**
	 * Update the calculated height and width of the rectangle
	 *
	 * @param {google.maps.Rectangle} rectangle
	 */
	private updateDimentions(rectangle: google.maps.Rectangle): void {
		let bounds = this.rectangle.getBounds().toJSON();

		let height = google.maps.geometry.spherical.computeDistanceBetween(
			new google.maps.LatLng(bounds.north, bounds.east),
			new google.maps.LatLng(bounds.south, bounds.east)
		);

		let width = google.maps.geometry.spherical.computeDistanceBetween(
			new google.maps.LatLng(bounds.north, bounds.east),
			new google.maps.LatLng(bounds.north, bounds.west)
		);

		this.dimensions = {
			height: height.toFixed(2),
			width: width.toFixed(2)
		};
	}

	/**
	 * Closes the Layer Setup interface and resets objects and data
	 */
	private async closeLayerSetup(data: any = null): Promise<void> {
		if (this.selectedMapLayerId) {
			this.toggleMapLayerImage(this.selectedMapLayerId, false);
		}

		this.clearMap();
		// reset editing state
		this.EditingLayer = false;

		// reset adding state
		this.AddingLayer = false;

		// remove the rectangle from the map
		if (this.rectangle) {
			this.rectangle.setMap(null);
		}

		// remove the preview layer from the map
		if (this.previewLayer) {
			this.previewLayer.remove();
		}

		// remove the drawing manager from the map
		if (this.drawingManager) {
			this.drawingManager.setMap(null);
		}

		if (data) {
			if (typeof data == "string") {
				// when a layer is saved
				if (data == "save") {
					let layerId = this.selectedMapLayerId;
					if (this.googleMapLayers[layerId]) {
						// clear the current overlay data to be created with any new changes
						this.googleMapLayers[layerId].remove();
						delete this.googleMapLayers[layerId];
					}

					this.setSelectedLayerId(null);
					let layerObj = this.findMapLayerById(layerId);

					if (layerObj !== undefined) {
						this.layersLoadStatus[layerObj.mapLayerId] = { layerID: layerObj.mapLayerId, isCurrentlyLoading: false, hasInitialised: false};
						await this.loadLayerItems(layerObj);
	
						this.gotoLayer(layerObj);
					}
					else
					{
						this.layersLoadStatus[layerObj.mapLayerId] = undefined;
					}

				}
			}
		}

		// reset selected layer id
		this.setSelectedLayerId(null);

		// reset layer data
		this.selectedLayer.reset();

		this.showInfoBox = false;
	}

	/**
	 * Updates any changes map item positions
	 */
	private saveChangesToMapLayerItems(): void {
		// if the map items were updated (ie moved around) then we need to save the positions
		if (this.mapItemsUpdated && this.mapItemsUpdated.length > 0) {
			this.savingMapLayerItems = true;
			let mapSetupLayer = this.selectedLayer;
			mapSetupLayer.layerItems.forEach(async (mapSetupLayerItem: MapSetupItem) => {
				if (this.mapItemsUpdated.find(f => f.itemId = mapSetupLayerItem.mapLayerItemID) != null) {
					if (!mapSetupLayerItem.mapLayerIds) {
						mapSetupLayerItem.mapLayerIds = [mapSetupLayer.mapLayerId];
					}
					await this.updateMapItem({ mapSetupLayer, mapSetupLayerItem });
				}
			});
			// reset the array ready for new changes
			this.mapItemsUpdated = [];

			// for animation purposes only
			setTimeout(() => {
				this.savingMapLayerItems = false;
			}, 200);
		}
	}

	/**
	 * Event handler that runs on emit from Layer Setup child component, on the upload of a layer image
	 * @param {string} src Object emitted from child component
	 */
	private previewLayerImageChange(src: string): void {
		// get the current bounds of the rectangle
		let rectangleBounds = this.rectangle.getBounds().toJSON();

		// create a bounds object for the overlay
		let bounds: ILatLngBounds = {
			north: rectangleBounds.north,
			south: rectangleBounds.south,
			west: rectangleBounds.west,
			east: rectangleBounds.east
		};

		if (this.previewLayer) {
			this.previewLayer.remove();
		}

		// create a new map overlay to preview the new map layer
		this.previewLayer = this.createOverlay(this.selectedLayer, bounds, src);

		// TODO : Need to find a way to know the layer has loaded
		setTimeout(() => {
			this.loadRotationElement();
		}, 100);
	}

	/**
	 * Helper method to update the bounds of the selected layer
	 *
	 * @param {ILatLngBounds} bounds - bounds object for coordinates
	 */
	private setSelectedLayerBounds(bounds: ILatLngBounds = null): void {
		if (!bounds) {
			bounds = this.rectangle.getBounds().toJSON();
		}

		this.selectedLayer.ne = `${bounds.north} ${bounds.east}`;
		this.selectedLayer.nw = `${bounds.north} ${bounds.west}`;
		this.selectedLayer.se = `${bounds.south} ${bounds.east}`;
		this.selectedLayer.sw = `${bounds.south} ${bounds.west}`;
	}

	/**
	 * Clear map of all layers and items
	 */
	private clearMap(): void {
		this.mapItemsUpdated = [];
		if (this.setupMap) {
			this.setupMap!.setCursor("grab");
		}
		this.hideRotationElements();

		this.closeInfoWindow();

		if (this.newMarker) {
			this.newMarker.remove();
		}

		if (this.drawingManager) {
			this.drawingManager.setMap(null);
		}

		if (this.rectangle) {
			this.rectangle.setMap(null);
		}

		if (this.previewLayer) {
			this.previewLayer.remove();
		}

		for (let mapLayerId in this.googleMapLayers) {
			if (this.googleMapLayers[mapLayerId]) {
				this.googleMapLayers[mapLayerId].visible = false;
				this.googleMapLayers[mapLayerId].remove();
			}
		}

		for (let mapLayerItemId in this.googleMapLayerItems) {
			if (this.googleMapLayerItems[mapLayerItemId]) {
				this.googleMapLayerItems[mapLayerItemId].visible = false;
				this.googleMapLayerItems[mapLayerItemId].remove();
			}
		}
	}

	/**
	 * On Viewport Changed Event Handler
	 */
	private onViewportChangedDelayed(bounds: any) {
		this.setRotationElementDimentions();
		if (this.selectedGroup.groupId > 0) {
			// TODO : Load near by areas
			// this.loadMapLayers(bounds).then(result => {
			// 	let layers = result.data.layers as MapLayerData[];
			// 	layers.forEach((layer: MapLayerData) => {
			// 		console.log(`${layer.groupID} - ${layer.title}`);
			// 	});
			// });
		}
	}

	/**
	 * Toggle visibility of a map layer image
	 *
	 * @param {number} mapLayerId
	 * @param {boolean | null} visibility
	 */
	private async toggleMapLayerImage(mapLayerId: number, visibility: boolean = null): Promise<void> {
		if (!visibility) {
			if (this.googleMapLayers[mapLayerId]) {
				visibility = !this.googleMapLayers[mapLayerId].visible;
			} else {
				visibility = false;
			}
		}

		let mapSetupLayer = this.findMapLayerById(mapLayerId);

		this.setLayerVisibility({ mapSetupLayer, visibility });
		if (this.googleMapLayers[mapLayerId]) {
			this.googleMapLayers[mapLayerId].visible = visibility;
		}
		if (!mapSetupLayer.layerItems || (mapSetupLayer.layerItems && mapSetupLayer.layerItems.length == 0)) {

			let items: MapItemData[] = await this.loadLayerLinkedOrUnlinkedItems(mapSetupLayer);

			// create google map item objects
			this.displayItems(items, mapSetupLayer);
		}

		mapSetupLayer.layerItems.forEach((item: MapSetupItem) => {
			if (this.googleMapLayerItems[item.mapLayerItemID]) {
				this.googleMapLayerItems[item.mapLayerItemID].visible = visibility;
			}
		});
	}

	/**
	 * Get text for Title tag referencing the image layer display state
	 *
	 * @param {number} mapLayerId
	 * @returns {string}
	 */
	private mapLayerImageStatusTitle(mapLayerId: number): string {
		if (this.selectedLayer.mapLayerId == mapLayerId) {
			return "";
		}

		let layer = this.findMapLayerById(mapLayerId);

		return (layer && layer.visible ? "hide" : "show") + " map layer image";
	}

	/**
	 * Vue Destroyed lifecycle hook
	 */
	private destroyed(): void {
		this.closeLayerSetup();
		this.clearMap();

		// reset data
		this.drawingManager = null;
		this.enhancedMapping = null;
		this.infoWindow = null;
		this.loading = false;
		this.savingMapLayerItems = false;
		this.mapCenter = null;
		this.previewLayer = null;
		this.rectangle = null;
		this.selectedGroup = null;
		this.selectedItemType = null;
		this.selectedLayer = null;
		this.setupMap = null;
		this.resetState();
	}

	/**
	 * Event handler for starting to draw a region (incomplete)
	 */
	private setRegion(data: any) {
		this.drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
		this.drawingManager.setMap(this.setupMap.map);
	}

	private getShortKey(mapItemType: MapItemTypeData) {
		// camera
		if (mapItemType.mapLayerItemTypeID == 1) {
			return "q";
		}
		// Audio
		if (mapItemType.mapLayerItemTypeID == 2) {
			return "w";
		}
		// Door
		if (mapItemType.mapLayerItemTypeID == 3) {
			return "e";
		}
		// Alarm
		if (mapItemType.mapLayerItemTypeID == 4) {
			return "r";
		}
		// Output
		if (mapItemType.mapLayerItemTypeID == 5) {
			return "y";
		}
	}

	private async getImageSize(src: string): Promise<ImageSize> {
		let img = new Image();
		let height: number;
		let width: number;

		return new Promise(resolve => {
			img.onload = function () {
				resolve({ height: img.height, width: img.width});
			}
			img.src = src;
		});
	}

	/**
	 * @summary Method which will setup a rectangle with an image,
	 * and setup the rec & image to the middle of the map.
	 * @param {mapImage} String reprents map byte[] data
	 * @returns {void}
	 */
	private async setRecImageOnMap(mapImage: string) {
		// Clear map before we do any logic.
		this.clearMap();

		// set editing status to true
		this.EditingLayer = true;

		// create a new rectangle to allow repositioning and sizing of the layer image
		this.rectangle = new google.maps.Rectangle({
			map: this.setupMap.map,
			editable: true,
			clickable: true,
		});

		// set the layer we are going to edit
		this.selectedLayer = new MapSetupLayer(null);

		const imageSize = await this.getImageSize(mapImage);
		const aspectRatio = imageSize.height/ imageSize.width;

		const center = new google.maps.LatLng(this.setupMap.center.lat, this.setupMap.center.lng);

		const seventyFiveMeters = 75;
		const zeroDegrees = 0;
		const ninetyDegrees = 90;

		const offsetNorth = google.maps.geometry.spherical.computeOffset(center, (aspectRatio * seventyFiveMeters), zeroDegrees);
		const offsetEast = google.maps.geometry.spherical.computeOffset(center, seventyFiveMeters, ninetyDegrees);

		const centerOffset = new google.maps.LatLng(offsetNorth.toJSON().lat, offsetEast.toJSON().lng);

		const centerBounds = new google.maps.LatLngBounds(center, centerOffset);

		this.rectangle.setBounds(centerBounds);
		this.rectangle.setOptions(this.rectangleOptions);

		// create bounds.
		const bounds = centerBounds.toJSON();
		this.selectedLayer.ne = `${bounds.north} ${bounds.east}`;
		this.selectedLayer.nw = `${bounds.north} ${bounds.west}`;
		this.selectedLayer.se = `${bounds.south} ${bounds.east}`;
		this.selectedLayer.sw = `${bounds.south} ${bounds.west}`;

		// hook up the event listener to respond to changes to the rectangle
		this.rectangle.addListener("bounds_changed", this.onRectangleBoundsChanged);
		this.previewLayerImageChange(mapImage);
		this.showMapLayers = true;
		this.showMapTools = false;
	}

	/**
	 * @summary Method which will enable the map tools & will close
	 * and remove the preview layer.
	 * @returns {void}
	 */
	private resetRecImage(): void {
		this.showMapTools = true;
		this.closeLayerSetup();
		this.clearMap();
	}

	/**
	 * @summary Computed property which will return a readonly instance of previewLayer visible
	 * @returns {boolean} returns the previewLayer visible status
	 */
	private get getPreviewLayerVisibility(): boolean {
		return this.previewLayer?.visible;
	}

	/**
	 * @summary Method which runs when a map conversion session has started
	 */
	private mapConversionStarted(): void {
		this.newlyConvertedMapLayer = null;
		this.rotateContainer = false;
		this.showMapLayers = false;
		this.showMapTools = false;
		this.showInfoBox = false;
	}

	/**
	 * @summary Method which runs when map conversion session finished
	 */
	private async mapConversionFinished(): Promise<void> {
		this.rotateContainer = true;
		this.showMapLayers = true;
		this.showMapTools = true;
		this.EditingLayer = false;
		this.showInfoBox = true;

		this.rotationContainer.style.transform = "rotate(0deg)";
	}

	/**
	 * @summary Method which runs to load a new map layer
	 */
	private async loadNewMapLayer(mapLayerID: number): Promise<void> {
		if (this.googleMapLayers[mapLayerID]) {
			// clear the current overlay data to be created with any new changes
			this.googleMapLayers[mapLayerID].remove();
			delete this.googleMapLayers[mapLayerID];
		}

		let layers: MapLayerData[] = await this.loadMapLayersByGroupID(this.selectedGroup.groupId);
		let newlyCreatedLayer = layers.find(l => l.mapLayerID === mapLayerID);
		if (newlyCreatedLayer) {
			let mapSetupLayer = new MapSetupLayer(newlyCreatedLayer);
			this.newlyConvertedMapLayer = mapSetupLayer;
			this.addUpdateMapSetupLayer(mapSetupLayer);
		}
	}

	/**
	 * @summary Method which goes to the newly converted layer if it exists
	 */
	private goToNewlyConvertedLayer(): void {
		if (this.newlyConvertedMapLayer) {
			this.gotoLayer(this.newlyConvertedMapLayer);
		}
	}

	/**
	 * @summary Method which resets the angle of the rotation container to 0 degrees
	 */
	private resetRotationContainer(): void {
		this.rotationContainer.style.transform = "rotate(0deg)";
	}

	/**
	 * @summary Method which updates the layers visibility
	 */
	private updateLayerVisibility(visible: boolean): void {
		if (visible) {
			this.imgLayerImage.style.display = "block";
			return;
		}

		this.imgLayerImage.style.display = "none";
	}

	/**
	 * @summary Method which runs when the rotation container is hidden or shown
	 */
	private updateRotationContainerVisibility(visible: boolean): void {
		if (visible) {
			if (this.rotationContainer) {
				this.rotationContainer.style.display = "block";
			}

			this.rectangle.setVisible(true);
			return;
		}

		this.rectangle.setVisible(false);
		this.rotationContainer.style.display = "none";
	}

	/**
	 * @summary If cancel triggered or clicked on the update layers item modal. This resets the icon locations
	 * not needing to re-load the layer and all icons.
	 */
	private cancelUpdateMapItems(): void {
		this.mapItemsUpdated.forEach(updateItem => {
			let currentItem = this.selectedLayer.layerItems.find(f => f.mapLayerItemID == updateItem.itemId);

			if(currentItem != null){
				currentItem.latLong = updateItem.oldLatLong;

				// Move on map
				if (this.googleMapLayerItems[currentItem.mapLayerItemID]) {
					this.googleMapLayerItems[currentItem.mapLayerItemID].moveTo(convertStringToLatLng(currentItem.latLong));
				}
			}
		});

		this.mapItemsUpdated = [];
	}

	private mapToConvertSelected(map: Map): void
	{
		this.mapToConvert = map;
	}


}
